// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { ThemeProvider } from './contexts/ThemeContext';
import { FlexiRequestProvider } from './contexts/FlexiRequestContext';
import { EmployeeProvider } from './contexts/EmployeeContext';
import { PayrollProvider } from './contexts/PayrollContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { HolidayProvider } from './contexts/HolidayContext';
import { AttendanceProvider } from './contexts/AttendanceContext';
import { LeaveProvider } from './contexts/LeaveContext';
import { ShiftProvider } from './contexts/ShiftContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { UserSettingsProvider } from './contexts/UserSettingsContext';
import { ToastProvider } from './contexts/ToastContext';
import { AIAvatarProvider } from './components/common/aia/AIAvatarContext';

import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import EmployeePortal from './components/employee/EmployeePortal';
import ManagerPortal from './components/manager-v2/ManagerPortal';
import AdminPortal from './components/admin/AdminPortal';
import MobileBlock from './components/common/MobileBlock';
import { cleanupLocalStorage } from './utils/cleanupLocalStorage';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useIsMobileBrowser } from './hooks/useIsMobileBrowser';
import './App.css';

function App() {
    // Phone browsers are not supported - short-circuit before any provider mounts
    const isMobile = useIsMobileBrowser();

    // Check for new app versions every 1 minute (auto-reload on deploy)
    useVersionCheck(60 * 1000);

    // Clean up localStorage on app load
    useEffect(() => {
        cleanupLocalStorage();
    }, []);

    if (isMobile) {
        return <MobileBlock />;
    }

    return (
        <AuthProvider>
            <ThemeProvider>
                <UserSettingsProvider>
                    <ToastProvider>
                        <NotificationProvider>
                            <EmployeeProvider>
                            <OnboardingProvider>
                                <HolidayProvider>
                                    <AttendanceProvider>
                                        <LeaveProvider>
                                            <ShiftProvider>
                                                <PayrollProvider>
                                                    <FlexiRequestProvider>
                                                        <AIAvatarProvider>
                                                            <Router>
                                                        <Routes>
                                                            {/* Public Routes */}
                                                            <Route path="/login" element={<Login />} />
                                                            <Route path="/reset-password" element={<ResetPasswordPage />} />

                                                            {/* Protected Routes */}
                                                            <Route path="/employee/*" element={
                                                                <ProtectedRoute>
                                                                    <EmployeePortal />
                                                                </ProtectedRoute>
                                                            } />

                                                            <Route path="/manager/*" element={
                                                                <ProtectedRoute requireManager>
                                                                    <ManagerPortal />
                                                                </ProtectedRoute>
                                                            } />

                                                            <Route path="/admin/*" element={
                                                                <ProtectedRoute requireAdmin>
                                                                    <AdminPortal />
                                                                </ProtectedRoute>
                                                            } />

                                                            {/* Default Redirect */}
                                                            <Route path="/" element={<Navigate to="/login" />} />
                                                        </Routes>
                                                    </Router>
                                                        </AIAvatarProvider>
                                                </FlexiRequestProvider>
                                            </PayrollProvider>
                                        </ShiftProvider>
                                    </LeaveProvider>
                                </AttendanceProvider>
                            </HolidayProvider>
                        </OnboardingProvider>
                    </EmployeeProvider>
                </NotificationProvider>
            </ToastProvider>
            </UserSettingsProvider>
            </ThemeProvider>
        </AuthProvider>
    );
}

export default App;
